import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table } from "antd";
import axios from "../../utils/axiosDash";
import {
  AlertOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CoffeeOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  EyeOutlined,
  LeftOutlined,
  PictureOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  ShopOutlined,
  TeamOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";

const STORE_STORAGE_KEY = "selectedStore";
// Change to match how your router is mounted (e.g. "/empire/get-empire-writer-alerts")
const ALERTS_ENDPOINT = "/get-empire-writer-alerts";
const LOGS_ENDPOINT = "/get-empire-writer-logs";
const FOOD_ALERTS_ENDPOINT = "/get-empire-writer-food-alert";
const AUTO_REFRESH_MS = 60000;
const ALERTS_PAGE_SIZE = 10; // rows per page in the alerts table
const STALE_AFTER_MIN = 5; // no check for this long => camera shown as "No recent data"
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DATE_OPTIONS_COUNT = 15; // how many days (today + previous) the date dropdown offers

// shift keys as sent by the logs API (data.shift1 / data.shift2)
const SHIFT_KEYS = ["shift1", "shift2"];
const SHIFT_LABEL = { shift1: "Shift 1", shift2: "Shift 2" };
const SHIFT_STYLE = {
  shift1: { chip: "bg-amber-50 text-amber-700", icon: "bg-amber-100 text-amber-600" },
  shift2: { chip: "bg-indigo-50 text-indigo-700", icon: "bg-indigo-100 text-indigo-600" },
};

// Fallback shift windows (24h, as stored). Start is inclusive, end is exclusive.
// The real timings come from the logs API (`shiftTimings`, per store, from EmpireSettings);
// these are only used until the API answers, or if a store has no timings.
// Shift 1: 11:00 AM -> 7:00 PM   (same day)
// Shift 2: 7:00 PM  -> 2:30 AM   (next day)
const DEFAULT_WINDOWS = {
  shift1: { start: "11:00", end: "19:00" },
  shift2: { start: "19:00", end: "02:30" },
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
/*
  All times are shown exactly as stored (UTC fields), with NO +5:30 / timezone shift.
  "now" is therefore also taken on the same wall-clock basis (see wallNow).
*/
const pad = (n) => String(n).padStart(2, "0");
const dateKeyOf = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

// current time expressed on the same "as stored" basis as the data:
// the UTC fields of the returned date equal the clock on the wall right now
const wallNow = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000);

const fmtDate = (d) =>
  d
    ? d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

const fmtTime = (d, withSeconds = true) =>
  d
    ? d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        ...(withSeconds ? { second: "2-digit" } : {}),
        timeZone: "UTC",
      })
    : "—";

const timeAgo = (d) => {
  if (!d) return "";
  const mins = Math.floor((wallNow().getTime() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

// 125 -> "2 hr 5 min", 45 -> "45 min"
const fmtMins = (m) => {
  if (!m) return "0 min";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

const dateLabel = (key) => {
  if (!key || typeof key !== "string") {
    return "—";
  }

  const today = wallNow();
  const yest = new Date(today.getTime() - 86400000);

  const parts = key.split("-");

  if (parts.length !== 3) {
    return "—";
  }

  const [y, m, d] = parts.map(Number);

  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) {
    return "—";
  }

  const text = fmtDate(new Date(Date.UTC(y, m - 1, d)));

  if (key === dateKeyOf(today)) {
    return `Today (${text})`;
  }

  if (key === dateKeyOf(yest)) {
    return `Yesterday (${text})`;
  }

  return text;
};

const cameraKeyOf = (r) => `${r.restaurant}|${r.cameraIp}|${r.channel}`;

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v?.$date || v);
  return isNaN(d) ? null : d;
};

/* ---------- date helpers ---------- */
// "2026-10-29" -> "2026-10-30"
const nextDateKey = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return dateKeyOf(new Date(Date.UTC(y, m - 1, d + 1)));
};

// today + previous days, newest first
const recentDateKeys = (count) => {
  const now = wallNow();
  return Array.from({ length: count }, (_, i) => dateKeyOf(new Date(now.getTime() - i * 86400000)));
};

// days to request for the chosen shift:
// shift 1 -> the selected day only
// shift 2 / all shifts -> the selected day AND the next day (shift 2 runs past midnight, until 2:30 AM)
const datesForShift = (dateKey, shiftValue) =>
  !dateKey ? [] : shiftValue === "shift1" ? [dateKey] : [dateKey, nextDateKey(dateKey)];

/* ---------- shift helpers ---------- */
// "19:00" -> 1140 (minutes since midnight)
const hhmmToMin = (s) => {
  if (!s || typeof s !== "string") return null;
  const [h, m] = s.split(":").map(Number);
  if (isNaN(h)) return null;
  return h * 60 + (m || 0);
};

// 1140 -> "7:00 PM"   (minutes since midnight, wraps past 24h)
const minToLabel = (mins) => {
  const total = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(total % 60)} ${h >= 12 ? "PM" : "AM"}`;
};

// "19:00" -> "7:00 PM"
const fmt12 = (s) => {
  const m = hhmmToMin(s);
  return m === null ? "—" : minToLabel(m);
};

// does the shift end on the next day? (e.g. 19:00 -> 02:30)
const crossesMidnight = (t) => {
  const s = hhmmToMin(t?.start);
  const e = hhmmToMin(t?.end);
  return s !== null && e !== null && e < s;
};

// {start:"11:00", end:"19:00"} -> "11:00 AM – 7:00 PM"
// {start:"19:00", end:"02:30"} -> "7:00 PM – 2:30 AM (next day)"
const shiftRangeLabel = (t) =>
  t ? `${fmt12(t.start)} – ${fmt12(t.end)}${crossesMidnight(t) ? " (next day)" : ""}` : "";

// length of a shift in minutes (handles shifts that cross midnight)
const shiftMinutes = (t) => {
  const s = hhmmToMin(t?.start);
  const e = hhmmToMin(t?.end);
  if (s === null || e === null) return 0;
  const diff = (e - s + 1440) % 1440;
  return diff === 0 ? 1440 : diff;
};

const isValidTiming = (t) => hhmmToMin(t?.start) !== null && hhmmToMin(t?.end) !== null;

// shift windows of one store, taken from the API's shiftTimings (falls back to the defaults)
const windowsFor = (timings, restaurant) => {
  const t = timings?.[restaurant];
  return t && isValidTiming(t.shift1) && isValidTiming(t.shift2) ? t : DEFAULT_WINDOWS;
};

// which shift does this moment fall in? (start inclusive, end exclusive; null = outside both shifts)

const shiftOfTime = (d, windows = DEFAULT_WINDOWS) => {
  if (!d || Number.isNaN(d.getTime())) return null;

  const mins = d.getUTCHours() * 60 + d.getUTCMinutes();

  for (const key of SHIFT_KEYS) {
    const timing = windows[key];
    const start = hhmmToMin(timing?.start);
    const end = hhmmToMin(timing?.end);

    if (start === null || end === null) continue;

    const inside =
      start < end
        ? mins >= start && mins < end
        : mins >= start || mins < end;

    if (inside) return key;
  }

  return null;
};

// a night shift belongs to the date on which it STARTED
// (e.g. a check at 12:30 AM of the 30th belongs to the 29th's Shift 2)
const shiftDateKeyOf = (d, shiftKey, windows = DEFAULT_WINDOWS) => {
  const t = windows[shiftKey];
  if (crossesMidnight(t)) {
    const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
    if (mins < hhmmToMin(t.start)) return dateKeyOf(new Date(d.getTime() - 86400000));
  }
  return dateKeyOf(d);
};

// every hour of a shift in order, with the minutes that actually belong to the shift.
// shift 2 (19:00 -> 02:30) -> 19,20,21,22,23,0,1 (60 min each) and 2 (only 30 min: 2:00 – 2:30)
const shiftHours = (shiftKey, windows = DEFAULT_WINDOWS) => {
  const t = windows[shiftKey];
  const start = hhmmToMin(t.start);
  const total = shiftMinutes(t);
  const end = start + total;
  const out = [];
  let cur = start;
  while (cur < end) {
    const hourEnd = (Math.floor(cur / 60) + 1) * 60;
    const chunkEnd = Math.min(hourEnd, end);
    out.push({
      hour: Math.floor(cur / 60) % 24,
      cap: chunkEnd - cur, // minutes of this hour that are part of the shift
      label: `${minToLabel(cur)} – ${minToLabel(chunkEnd)}`,
    });
    cur = chunkEnd;
  }
  return out;
};

// writer logs -> one flat list of per-minute checks.
// Both arrays (shift1 + shift2) are merged and every check is placed into a shift by its own
// checkTime (using that store's shift timings), so nothing depends on which array the backend put it in.
// Uses checkTime only (never createdAt). Duplicates (same camera + same checkTime) are removed.
const flattenLogs = (docs, timings) => {
  const seen = new Set();
  const out = [];

  (docs || []).forEach((doc) => {
    const windows = windowsFor(timings, doc.restaurant);

    (doc.minuteData || []).forEach((m) => {
      // Classify each minute using its own timestamp.
      const time = toDate(m.checkTime);

      if (!time) {
        return;
      }

      const shiftKey = shiftOfTime(time, windows);

      // Ignore records outside the configured shifts.
      if (!shiftKey) {
        return;
      }

      // After midnight, Shift 2 belongs to the previous day's shift.
      const dateKey = shiftDateKeyOf(time, shiftKey, windows);

      const row = {
        restaurant: doc.restaurant || "Unknown store",
        cameraIp: doc.cameraIp || "—",
        channel: doc.channel ?? "—",
        time,
        hour: time.getUTCHours(),
        shift: shiftKey,
        dateKey,
        state:
          m.writerState === "PRESENT" ? "PRESENT" : "ABSENT",
        confidence: Number(m.confidence) || 0,
      };

      const id = `${cameraKeyOf(row)}|${time.getTime()}`;

      if (seen.has(id)) {
        return;
      }

      seen.add(id);
      out.push(row);
    });
  });

  return out;
};

// alerts use only postgresCreatedAt (never alertTime / createdAt)
const normalizeAlert = (a) => {
  const time = toDate(a.postgresCreatedAt);

  return {
    id: a._id,
    restaurant: a.restaurant || "Unknown store",
    cameraIp: a.cameraIp || "—",
    channel: a.channel ?? "—",
    type: a.alertType || "WRITER_ABSENT",
    checks: a.consecutiveAbsentChecks || 0,
    image: a.snapshotPath || "",
    // Exact postgresCreatedAt date/time
    time,
    // Date key based ONLY on postgresCreatedAt
    dateKey: time ? dateKeyOf(time) : "",
    postgresCreatedAt: a.postgresCreatedAt || null,
  };
};

// food alerts use ONLY postgresCreatedAt (never createdAt / clickedAt)
const normalizeFoodAlert = (a) => {
  const time = toDate(a.postgresCreatedAt);
  return {
    id: a._id?.$oid || a._id || `${a.postgresId}-${a.foodName}`,
    restaurant: a.restaurant || "Unknown store",
    foodName: a.foodName || "—",
    image: a.imagePath || "",
    time,
    dateKey: time ? dateKeyOf(time) : "",
  };
};

const rateTone = (pct) =>
  pct === null
    ? { text: "text-slate-400", bg: "bg-slate-100" }
    : pct >= 80
    ? { text: "text-emerald-600", bg: "bg-emerald-50" }
    : pct >= 50
    ? { text: "text-amber-600", bg: "bg-amber-50" }
    : { text: "text-rose-600", bg: "bg-rose-50" };

/* ------------------------------------------------------------------ */
/* Small UI pieces                                                     */
/* ------------------------------------------------------------------ */
const StatTile = ({ icon, label, value, hint, tone }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-lg ${tone}`}>{icon}</span>
    <div className="min-w-0">
      <p className="text-2xl font-semibold leading-none text-slate-800">{value}</p>
      <p className="mt-1 truncate text-sm text-slate-600">{label}</p>
      {hint && <p className="truncate text-xs text-slate-400">{hint}</p>}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    PRESENT: { cls: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500", text: "Present" },
    ABSENT: { cls: "bg-rose-50 text-rose-700", dot: "bg-rose-500", text: "Absent" },
    STALE: { cls: "bg-slate-100 text-slate-500", dot: "bg-slate-400", text: "No recent data" },
  };
  const s = map[status] || map.STALE;
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${s.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.text}
    </span>
  );
};

// Present / Absent / No-check split of one hour as a single bar.
// `capacity` is the number of minutes of that hour that belong to the shift (60, or less for a
// partial hour such as 2:00 – 2:30 AM at the end of shift 2).
const SplitBar = ({ present, absent, capacity = 60 }) => {
  const base = Math.max(capacity, present + absent, 1);
  const none = Math.max(base - present - absent, 0);
  return (
    <div
      className="flex h-2.5 w-full min-w-[120px] overflow-hidden rounded-full bg-slate-100"
      title={`${present} min present · ${absent} min absent · ${none} min no check`}
    >
      <span className="bg-emerald-400" style={{ width: `${(present / base) * 100}%` }} />
      <span className="bg-rose-400" style={{ width: `${(absent / base) * 100}%` }} />
    </div>
  );
};

// Present / Absent / No-check split of a whole shift as a single bar
const ShiftBar = ({ present, absent, total }) => {
  const base = Math.max(total || 0, present + absent, 1);
  const none = Math.max(base - present - absent, 0);
  return (
    <div
      className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100"
      title={`${fmtMins(present)} present · ${fmtMins(absent)} absent · ${fmtMins(none)} no check`}
    >
      <span className="bg-emerald-400" style={{ width: `${(present / base) * 100}%` }} />
      <span className="bg-rose-400" style={{ width: `${(absent / base) * 100}%` }} />
    </div>
  );
};

// one shift's summary inside the camera table row
const ShiftCell = ({ data }) => {
  if (!data || !data.hasData) {
    return <span className="text-xs text-slate-400">No data</span>;
  }
  const checked = data.present + data.absent;
  const pct = checked ? Math.round((data.present / checked) * 100) : null;
  const tone = rateTone(pct);
  return (
    <div className="min-w-[190px]">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className={`rounded-md px-1.5 py-0.5 text-xs font-semibold ${tone.bg} ${tone.text}`}>
          {pct === null ? "—" : `${pct}% present`}
        </span>
        {data.alerts > 0 && (
          <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700">
            <AlertOutlined /> {data.alerts}
          </span>
        )}
      </div>
      <ShiftBar present={data.present} absent={data.absent} total={data.totalMins} />
      <p className="mt-1 text-xs text-slate-500">
        <span className="font-medium text-emerald-600">{fmtMins(data.present)}</span>
        {" · "}
        <span className="font-medium text-rose-600">{fmtMins(data.absent)}</span>
      </p>
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold text-slate-500 ${className}`}>{children}</th>
);

const SectionCard = ({ icon, title, subtitle, right, children }) => (
  <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
    <header className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-600">{icon}</span>
        <div>
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {right}
    </header>
    {children}
  </section>
);

const EmptyRow = ({ colSpan, title, text, ok }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-14 text-center">
      {ok ? (
        <CheckCircleOutlined className="text-3xl text-emerald-400" />
      ) : (
        <ExclamationCircleOutlined className="text-3xl text-slate-300" />
      )}
      <p className="mt-2 font-medium text-slate-700">{title}</p>
      <p className="text-sm text-slate-500">{text}</p>
    </td>
  </tr>
);

// empty state used inside the antd Table (locale.emptyText)
const EmptyBlock = ({ title, text, ok }) => (
  <div className="px-4 py-10 text-center">
    {ok ? (
      <CheckCircleOutlined className="text-3xl text-emerald-400" />
    ) : (
      <ExclamationCircleOutlined className="text-3xl text-slate-300" />
    )}
    <p className="mt-2 font-medium text-slate-700">{title}</p>
    <p className="text-sm text-slate-500">{text}</p>
  </div>
);

// compact page list: 1 … 4 5 6 … 20
const pageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
};

const Pagination = ({ page, pageSize, total, onChange }) => {
  const totalPages = Math.max(Math.ceil(total / pageSize), 1);
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const btn =
    "flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg border px-2.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-40";
  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <p className="text-xs text-slate-500">
        Showing {from}–{to} of {total}
      </p>
      <nav className="flex flex-wrap items-center gap-1.5" aria-label="Pagination">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          className={`${btn} border-slate-200 bg-white text-slate-600 hover:bg-slate-50`}
        >
          <LeftOutlined />
        </button>
        {pageList(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`gap-${i}`} className="px-1 text-slate-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              aria-current={p === page ? "page" : undefined}
              className={`${btn} ${
                p === page
                  ? "border-sky-300 bg-sky-50 font-semibold text-sky-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          className={`${btn} border-slate-200 bg-white text-slate-600 hover:bg-slate-50`}
        >
          <RightOutlined />
        </button>
      </nav>
    </div>
  );
};

const AlertImage = ({ src, alt, className = "" }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return (
      <div className={`flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-400 ${className}`}>
        <PictureOutlined className="text-lg" />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={`object-cover ${className}`} />;
};

const ImageModal = ({ alert, onClose, alt = "Snapshot", details = [] }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-3 backdrop-blur-sm sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-800">{alert.restaurant}</h3>
            <p className="truncate text-xs text-slate-500">
              {fmtDate(alert.time)} at {fmtTime(alert.time)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {alert.image && (
              <a
                href={alert.image}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50"
              >
                <ExportOutlined /> Open
              </a>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100"
            >
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className="overflow-y-auto">
          <AlertImage src={alert.image} alt={alt} className="max-h-[60vh] min-h-[200px] w-full bg-slate-50 object-contain" />
          {details.length > 0 && (
            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 sm:p-5">
              {details.map(([k, v]) => (
                <div key={k} className="rounded-lg bg-slate-50 p-3">
                  <p className="text-xs text-slate-400">{k}</p>
                  <p className="mt-0.5 truncate text-sm font-medium text-slate-700">{v}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */
const EmpireWriterAlerts = () => {
  const [store, setStore] = useState(localStorage.getItem(STORE_STORAGE_KEY) || "All Stores");
  const [alerts, setAlerts] = useState([]);
  const [checks, setChecks] = useState([]);
  // per-store shift timings sent by the logs API: { [store]: { shift1: {start,end}, shift2: {start,end} } }
  const [shiftTimings, setShiftTimings] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errors, setErrors] = useState({ alerts: "", logs: "" });
  const [search, setSearch] = useState("");
  const [date, setDate] = useState(() => dateKeyOf(wallNow())); // defaults to today
  const [camera, setCamera] = useState("all");
  const [shift, setShift] = useState("all"); // all | shift1 | shift2
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({}); // camera key -> bool

  // writer food alerts
  const [foodAlerts, setFoodAlerts] = useState([]);
  const [foodLoading, setFoodLoading] = useState(false);
  const [foodRefreshing, setFoodRefreshing] = useState(false);
  const [foodError, setFoodError] = useState("");
  const [foodPage, setFoodPage] = useState(1);
  const [foodPageSize, setFoodPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [selectedFood, setSelectedFood] = useState(null);
  const foodReqId = useRef(0);
  const logsReqId = useRef(0);
  const [alertPage, setAlertPage] = useState(1);

  // the selected date (dropdown always offers today + previous days)
  const dates = useMemo(() => recentDateKeys(DATE_OPTIONS_COUNT), []);
  const activeDate = date || dates[0] || "";

  // shift windows used for the dropdown / table headers:
  // the selected store's timings, otherwise the first store the API returned, otherwise the defaults
  const displayWindows = useMemo(() => {
    const own = shiftTimings[store];
    if (own && isValidTiming(own.shift1) && isValidTiming(own.shift2)) return own;
    const first = Object.values(shiftTimings).find((t) => t && isValidTiming(t.shift1) && isValidTiming(t.shift2));
    return first || DEFAULT_WINDOWS;
  }, [shiftTimings, store]);

  const shiftRanges = useMemo(
    () => ({
      shift1: shiftRangeLabel(displayWindows.shift1),
      shift2: shiftRangeLabel(displayWindows.shift2),
    }),
    [displayWindows]
  );

  const fetchAll = useCallback(
    async (silent = false) => {
      const reqId = ++logsReqId.current;
      silent ? setRefreshing(true) : setLoading(true);

      // shift 1 -> [date]; shift 2 / all shifts -> [date, next day]
      // (shift 2 starts at 7:00 PM on `date` and ends at 2:30 AM on the next day)
      const days = datesForShift(activeDate, shift);
      const logsBody = {
        store,
        shift, // "all" | "shift1" | "shift2"
        date: activeDate,
        dates: days,
        startDate: days[0],
        endDate: days[days.length - 1],
      };

      const [alertsRes, logsRes] = await Promise.allSettled([
        axios.post(ALERTS_ENDPOINT, { store }),
        axios.post(LOGS_ENDPOINT, logsBody),
      ]);
      if (reqId !== logsReqId.current) return; // a newer request replaced this one

      const next = { alerts: "", logs: "" };
      const pick = (res, key, label) => {
        if (res.status === "fulfilled" && res.value?.data?.success) return res.value.data.data || [];
        next[key] =
          (res.status === "rejected" && (res.reason?.response?.data?.message || res.reason?.message)) ||
          res.value?.data?.message ||
          `Could not load ${label}.`;
        return null;
      };

      const alertDocs = pick(alertsRes, "alerts", "writer alerts");

      // logs come shift-wise: { success, shift1: [...], shift2: [...], shiftTimings: { [store]: {...} } }
      // both arrays are merged; every check is placed into its shift by its own checkTime,
      // using that store's shift timings from the API
      let logChecks = null;
      let timings = null;
      if (logsRes.status === "fulfilled" && logsRes.value?.data?.success) {
        const body = logsRes.value.data;
        timings = body.shiftTimings || {};
        logChecks = flattenLogs([...(body.shift1 || []), ...(body.shift2 || [])], timings);
      } else {
        next.logs =
          (logsRes.status === "rejected" && (logsRes.reason?.response?.data?.message || logsRes.reason?.message)) ||
          logsRes.value?.data?.message ||
          "Could not load presence logs.";
      }

      if (alertDocs) setAlerts(alertDocs.map(normalizeAlert));
      if (timings) setShiftTimings(timings);
      if (logChecks) setChecks(logChecks);
      setErrors(next);
      if (alertDocs || logChecks) setLastUpdated(wallNow());
      setLoading(false);
      setRefreshing(false);
    },
    [store, activeDate, shift]
  );

  // fetches on mount and again whenever the store, date or shift changes
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Pick up store changes made elsewhere in the app
  useEffect(() => {
    const sync = () => {
      const next = localStorage.getItem(STORE_STORAGE_KEY) || "All Stores";
      setStore((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  // Reset camera filter when the store changes
  useEffect(() => {
    setCamera("all");
  }, [store]);

  /* ---------- writer food alerts: fetch by store + date ---------- */
  const fetchFood = useCallback(
  async (silent = false) => {
    if (!activeDate) {
      setFoodAlerts([]);
      setFoodError("");
      return;
    }
    const reqId = ++foodReqId.current;
    silent ? setFoodRefreshing(true) : setFoodLoading(true);
    try {
      const days = datesForShift(activeDate, shift);
      const results = await Promise.allSettled(
        days.map((d) => axios.post(FOOD_ALERTS_ENDPOINT, { storeName: store, date: d }))
      );
      if (reqId !== foodReqId.current) return;

      const ok = results.filter((r) => r.status === "fulfilled" && r.value?.data?.success);
      if (ok.length === 0) {
        const first = results[0];
        setFoodError(
          (first.status === "rejected" && (first.reason?.response?.data?.message || first.reason?.message)) ||
            first.value?.data?.message ||
            "Could not load writer food alerts."
        );
      } else {
        const rows = ok.flatMap((r) => r.value.data.data || []).map(normalizeFoodAlert);
        // remove duplicates in case both days return the same record
        setFoodAlerts([...new Map(rows.map((f) => [f.id, f])).values()]);
        setFoodError("");
      }
    } finally {
      if (reqId === foodReqId.current) {
        setFoodLoading(false);
        setFoodRefreshing(false);
      }
    }
  },
  [store, activeDate, shift]
);

  useEffect(() => {
    fetchFood();
  }, [fetchFood]);

  // auto refresh both datasets
  useEffect(() => {
    const id = setInterval(() => {
      fetchAll(true);
      fetchFood(true);
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchAll, fetchFood]);

  const cameraOptions = useMemo(() => {
    const map = new Map();
    [...checks, ...alerts].forEach((r) => {
      const k = cameraKeyOf(r);
      if (!map.has(k)) map.set(k, `${r.restaurant} · Ch ${r.channel} (${r.cameraIp})`);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [checks, alerts]);

  const matchesSearch = useCallback(
    (r) => {
      const q = search.trim().toLowerCase();
      const okText =
        !q || [r.restaurant, r.cameraIp, `channel ${r.channel}`].some((v) => String(v).toLowerCase().includes(q));
      return okText && (camera === "all" || cameraKeyOf(r) === camera);
    },
    [search, camera]
  );

  // shifts currently shown (driven by the shift filter)
  const visibleShifts = useMemo(() => (shift === "all" ? SHIFT_KEYS : [shift]), [shift]);

  const cameras = useMemo(() => {
    const map = new Map();
    const ensure = (r) => {
      const k = cameraKeyOf(r);
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          restaurant: r.restaurant,
          cameraIp: r.cameraIp,
          channel: r.channel,
          all: [],
          // checks / alerts of the active date, grouped by shift
          byShift: { shift1: [], shift2: [] },
          alertsByShift: { shift1: [], shift2: [] },
          unassignedAlerts: [],
        });
      }
      return map.get(k);
    };

    checks.filter(matchesSearch).forEach((c) => {
      const cam = ensure(c);
      cam.all.push(c);

      if (c.dateKey === activeDate && cam.byShift[c.shift]) {
        cam.byShift[c.shift].push(c);
      }
    });

    alerts.filter(matchesSearch).forEach((a) => {
      if (!a.time) return;

      // attach the alert to the shift it happened in (and to the date that shift started on)
      const windows = windowsFor(shiftTimings, a.restaurant);
      const shiftKey = shiftOfTime(a.time, windows);
      if (shiftKey) {
        if (shiftDateKeyOf(a.time, shiftKey, windows) === activeDate) ensure(a).alertsByShift[shiftKey].push(a);
      } else if (a.dateKey === activeDate) {
        ensure(a).unassignedAlerts.push(a); // alert outside both shift windows
      }
    });

    const now = wallNow().getTime();
    const list = [...map.values()].map((cam) => {
      // latest check by checkTime
      const lastCheck = cam.all.reduce((acc, c) => (!acc || c.time > acc.time ? c : acc), null);
      const status =
        !lastCheck || now - lastCheck.time.getTime() > STALE_AFTER_MIN * 60000 ? "STALE" : lastCheck.state;

      const windows = windowsFor(shiftTimings, cam.restaurant);

      // one summary per shift, each with its full hour-by-hour breakdown
      const shifts = visibleShifts.map((key) => {
        const timing = windows[key];
        const dayChecks = cam.byShift[key];
        const dayAlerts = cam.alertsByShift[key];

        // every hour of the shift is listed, even when it has no checks
        const rows = shiftHours(key, windows).map((h) => ({
          hour: h.hour,
          label: h.label,
          cap: h.cap,
          present: 0,
          absent: 0,
          alerts: 0,
        }));
        const byHour = new Map(rows.map((r) => [r.hour, r]));

        dayChecks.forEach((c) => {
          const row = byHour.get(c.hour);
          if (!row) return;
          c.state === "PRESENT" ? row.present++ : row.absent++;
        });
        dayAlerts.forEach((a) => {
          const row = byHour.get(a.time.getUTCHours());
          if (row) row.alerts++;
        });

        const present = dayChecks.filter((c) => c.state === "PRESENT").length;
        return {
          key,
          label: SHIFT_LABEL[key],
          timing,
          range: shiftRangeLabel(timing),
          totalMins: shiftMinutes(timing),
          hasData: dayChecks.length > 0,
          present,
          absent: dayChecks.length - present,
          alerts: dayAlerts.length,
          hours: rows,
        };
      });

      const present = shifts.reduce((s, x) => s + x.present, 0);
      const absent = shifts.reduce((s, x) => s + x.absent, 0);
      const alertCount =
        shifts.reduce((s, x) => s + x.alerts, 0) + (shift === "all" ? cam.unassignedAlerts.length : 0);
      return {
        ...cam,
        lastCheck,
        status,
        shifts,
        present,
        absent,
        alertCount,
      };
    });

    const weight = { ABSENT: 0, PRESENT: 1, STALE: 2 };
    return list.sort(
      (a, b) =>
        weight[a.status] - weight[b.status] ||
        a.restaurant.localeCompare(b.restaurant) ||
        String(a.channel).localeCompare(String(b.channel), undefined, { numeric: true })
    );
  }, [checks, alerts, matchesSearch, activeDate, visibleShifts, shift, shiftTimings]);

  // alerts that belong to the selected date + shift
  // (a shift 2 alert after midnight, up to 2:30 AM, belongs to the date the shift started)
  const filteredAlerts = useMemo(
    () =>
      alerts
        .filter(matchesSearch)
        .filter((a) => {
          if (!activeDate) return true;
          if (!a.time) return false;
          const windows = windowsFor(shiftTimings, a.restaurant);
          const shiftKey = shiftOfTime(a.time, windows);
          if (shiftKey) {
            if (!visibleShifts.includes(shiftKey)) return false;
            return shiftDateKeyOf(a.time, shiftKey, windows) === activeDate;
          }
          return shift === "all" && a.dateKey === activeDate;
        })
        .sort((a, b) => (b.time?.getTime() || 0) - (a.time?.getTime() || 0)),
    [alerts, matchesSearch, activeDate, visibleShifts, shift, shiftTimings]
  );

  // food alerts: search by store / food name, newest first (by postgresCreatedAt)
  const filteredFood = useMemo(() => {
  const q = search.trim().toLowerCase();
  return foodAlerts
    .filter((f) => {
      if (q && ![f.restaurant, f.foodName].some((v) => String(v).toLowerCase().includes(q))) return false;
      if (!activeDate) return true;
      if (!f.time) return false;
      const windows = windowsFor(shiftTimings, f.restaurant);
      const shiftKey = shiftOfTime(f.time, windows);
      if (shiftKey) {
        if (!visibleShifts.includes(shiftKey)) return false;
        return shiftDateKeyOf(f.time, shiftKey, windows) === activeDate;
      }
      return shift === "all" && f.dateKey === activeDate;
    })
    .sort((a, b) => (b.time?.getTime() || 0) - (a.time?.getTime() || 0));
}, [foodAlerts, search, activeDate, visibleShifts, shift, shiftTimings]);

  // go back to page 1 whenever the data set changes
  useEffect(() => {
  setFoodPage(1);
}, [store, activeDate, shift, search]);

  // antd columns for the Writer Food Alerts table
  const foodColumns = useMemo(
    () => [
      {
        title: "Date",
        key: "date",
        render: (_, f) => <span className="whitespace-nowrap text-slate-600">{fmtDate(f.time)}</span>,
      },
      {
        title: "Time",
        key: "time",
        sorter: (a, b) => (a.time?.getTime() || 0) - (b.time?.getTime() || 0),
        render: (_, f) => <span className="whitespace-nowrap font-medium text-slate-800">{fmtTime(f.time)}</span>,
      },
      {
        title: "Store",
        dataIndex: "restaurant",
        key: "restaurant",
        render: (v) => <span className="font-medium text-slate-800">{v}</span>,
      },
      {
        title: "Food name",
        dataIndex: "foodName",
        key: "foodName",
        render: (v) => (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
            <CoffeeOutlined />
            {/* {v} */}
            Food and Beverage
          </span>
        ),
      },
      {
        title: "Image",
        key: "image",
        render: (_, f) => (
          <button
            type="button"
            onClick={() => setSelectedFood(f)}
            className="group flex items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
            aria-label={`View image for ${f.foodName}`}
          >
            <AlertImage src={f.image} alt={f.foodName} className="h-10 w-16 rounded-md border border-slate-200" />
            <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 group-hover:underline">
              <EyeOutlined /> View
            </span>
          </button>
        ),
      },
    ],
    [foodPage, foodPageSize]
  );

  // back to page 1 whenever the filters / store change
  useEffect(() => {
    setAlertPage(1);
  }, [search, camera, date, shift, store]);

  const alertTotalPages = Math.max(Math.ceil(filteredAlerts.length / ALERTS_PAGE_SIZE), 1);
  const safeAlertPage = Math.min(alertPage, alertTotalPages);
  const pagedAlerts = useMemo(
    () => filteredAlerts.slice((safeAlertPage - 1) * ALERTS_PAGE_SIZE, safeAlertPage * ALERTS_PAGE_SIZE),
    [filteredAlerts, safeAlertPage]
  );

  const summary = useMemo(() => {
    const present = cameras.reduce((s, c) => s + c.present, 0);
    const absent = cameras.reduce((s, c) => s + c.absent, 0);
    return {
      present,
      absent,
      absentNow: cameras.filter((c) => c.status === "ABSENT").length,
      total: cameras.length,
    };
  }, [cameras]);

  const bothFailed = errors.alerts && errors.logs;
  const hasAnyData = checks.length > 0 || alerts.length > 0;
  const toggle = (k) => setExpanded((p) => ({ ...p, [k]: !p[k] }));
  const shiftHint = shift === "all" ? "All shifts" : SHIFT_LABEL[shift];
  const tableCols = 4 + visibleShifts.length; // chevron, store, status, shifts…, alerts, last check

  const selectCls =
    "rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100";

  /* ---------- render ---------- */
  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl space-y-5">
        {/* Title */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-100 text-xl text-sky-600">
              <TeamOutlined />
            </span>
            <div>
              <h1 className="text-xl font-semibold text-slate-800 sm:text-2xl">Writer Presence</h1>
              <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-slate-500">
                <ShopOutlined /> {store}
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              fetchAll(true);
              fetchFood(true);
            }}
            disabled={loading || refreshing || foodRefreshing}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-4 py-2.5 text-sm font-medium text-sky-700 transition hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <ReloadOutlined spin={refreshing || foodRefreshing} /> Refresh
          </button>
        </div>

        {/* Filters (apply to the whole page) */}
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 lg:flex-row lg:items-center lg:justify-between">
          <label className="relative block lg:w-96">
            <SearchOutlined className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by store, camera IP or channel"
              className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-700 placeholder-slate-400 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            />
          </label>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select value={activeDate} onChange={(e) => setDate(e.target.value)} aria-label="Date" className={selectCls}>
              {dates.map((d) => (
                <option key={d} value={d}>
                  {dateLabel(d)}
                </option>
              ))}
            </select>
            <select value={shift} onChange={(e) => setShift(e.target.value)} aria-label="Shift" className={selectCls}>
              <option value="all">All shifts</option>
              {SHIFT_KEYS.map((k) => (
                <option key={k} value={k}>
                  {SHIFT_LABEL[k]} ({shiftRanges[k]})
                </option>
              ))}
            </select>
            {/* <select value={camera} onChange={(e) => setCamera(e.target.value)} aria-label="Camera" className={selectCls}>
              <option value="all">All cameras</option>
              {cameraOptions.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </select> */}
          </div>
        </div>

        {/* Partial-failure notice */}
        {!bothFailed && (errors.alerts || errors.logs) && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <ExclamationCircleOutlined className="mt-0.5" />
            <p>{errors.logs || errors.alerts} Showing the data that did load.</p>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile
            icon={<CheckCircleOutlined />}
            label="Present"
            value={fmtMins(summary.present)}
            hint={activeDate ? `${dateLabel(activeDate)} · ${shiftHint}` : "No data yet"}
            tone="bg-emerald-50 text-emerald-600"
          />
          <StatTile
            icon={<UserDeleteOutlined />}
            label="Absent"
            value={fmtMins(summary.absent)}
            hint={activeDate ? `${dateLabel(activeDate)} · ${shiftHint}` : undefined}
            tone="bg-rose-50 text-rose-500"
          />
          <StatTile
            icon={<CameraOutlined />}
            label="Absent right now"
            value={summary.absentNow}
            hint={`of ${summary.total} Store${summary.total === 1 ? "" : "s"}`}
            tone="bg-amber-50 text-amber-600"
          />
          <StatTile
            icon={<AlertOutlined />}
            label="Alerts raised"
            value={filteredAlerts.length}
            hint={activeDate ? `${dateLabel(activeDate)} · ${shiftHint}` : undefined}
            tone="bg-sky-50 text-sky-600"
          />
        </div>

        {bothFailed ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-rose-100 bg-rose-50/60 px-6 py-12 text-center">
            <ExclamationCircleOutlined className="text-3xl text-rose-400" />
            <p className="font-medium text-slate-700">We couldn't load the writer data</p>
            <p className="max-w-md text-sm text-slate-500">{errors.logs || errors.alerts}</p>
            <button
              onClick={() => fetchAll()}
              className="mt-1 rounded-lg bg-white px-4 py-2 text-sm font-medium text-rose-600 ring-1 ring-rose-200 transition hover:bg-rose-50"
            >
              Try again
            </button>
          </div>
        ) : loading ? (
          <div className="space-y-5">
            {[0, 1].map((i) => (
              <div key={i} className="animate-pulse rounded-xl border border-slate-200 bg-white p-5">
                <div className="mb-4 h-6 w-48 rounded bg-slate-100" />
                {Array.from({ length: 4 }).map((_, j) => (
                  <div key={j} className="mb-2 h-9 rounded bg-slate-50" />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* ---------------- Camera status table (shift-wise) ---------------- */}
            <SectionCard
              icon={<CameraOutlined />}
              title="Writer Presence Absence"
              subtitle={`Writer presence per camera, shift by shift, for ${
                activeDate ? dateLabel(activeDate) : "—"
              }${
                shift !== "shift1" && activeDate
                  ? ` (Shift 2 runs into ${dateLabel(nextDateKey(activeDate))}, up to ${fmt12(displayWindows.shift2.end)})`
                  : ""
              }. Click a row to see hour-by-hour details.`}
              right={
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> Present
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /> Absent
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-slate-200" /> No check
                  </span>
                </div>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50/40">
                    <tr>
                      <Th className="w-10" />
                      <Th>Store</Th>
                      <Th>Current status</Th>
                      {visibleShifts.map((k) => (
                        <Th key={k}>
                          <span className="flex items-center gap-1.5">
                            <ClockCircleOutlined /> {SHIFT_LABEL[k]}
                          </span>
                          <span className="mt-0.5 block text-[11px] font-normal text-slate-400">{shiftRanges[k]}</span>
                        </Th>
                      ))}
                      <Th>Last Check</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cameras.length === 0 ? (
                      <EmptyRow
                        colSpan={tableCols}
                        title={hasAnyData ? "No cameras match your filters" : "No presence data yet"}
                        text={
                          hasAnyData
                            ? "Try a different search or camera."
                            : `Checks will appear here once cameras at ${store} start reporting.`
                        }
                      />
                    ) : (
                      cameras.map((cam) => {
                        const open = !!expanded[cam.key];
                        return (
                          <Fragment key={cam.key}>
                            <tr
                              onClick={() => toggle(cam.key)}
                              className={`cursor-pointer transition hover:bg-sky-50/40 ${open ? "bg-sky-50/40" : ""}`}
                            >
                              <td className="px-4 py-3 text-xs text-slate-400">{open ? <DownOutlined /> : <RightOutlined />}</td>
                              <td className="px-4 py-3 font-medium text-slate-800">{cam.restaurant}</td>

                              <td className="px-4 py-3">
                                <StatusPill status={cam.status} />
                              </td>
                              {cam.shifts.map((s) => (
                                <td key={s.key} className="px-4 py-3 align-top">
                                  <ShiftCell data={s} />
                                </td>
                              ))}
                              <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                                {cam.lastCheck ? timeAgo(cam.lastCheck.time) : "—"}
                              </td>
                            </tr>

                            {open && (
                              <tr className="bg-slate-50/60">
                                <td colSpan={tableCols} className="px-4 py-4 sm:px-8">
                                  <div className="space-y-4">
                                    {cam.shifts.map((s) => {
                                      const checked = s.present + s.absent;
                                      const pct = checked ? Math.round((s.present / checked) * 100) : null;
                                      const tone = rateTone(pct);
                                      const style = SHIFT_STYLE[s.key];
                                      return (
                                        <div key={s.key} className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                                          {/* shift header */}
                                          <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center gap-3">
                                              <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${style.icon}`}>
                                                <ClockCircleOutlined />
                                              </span>
                                              <div>
                                                <p className="text-sm font-semibold text-slate-800">{s.label}</p>
                                                <p className="text-xs text-slate-500">
                                                  {s.range}
                                                  {s.totalMins ? ` · ${fmtMins(s.totalMins)}` : ""}
                                                </p>
                                              </div>
                                            </div>
                                            {(s.hasData || s.alerts > 0) && (
                                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span className={`rounded-md px-2 py-1 font-semibold ${tone.bg} ${tone.text}`}>
                                                  {pct === null ? "—" : `${pct}% present`}
                                                </span>
                                                <span className="rounded-md bg-emerald-50 px-2 py-1 font-medium text-emerald-700">
                                                  Present {fmtMins(s.present)}
                                                </span>
                                                <span className="rounded-md bg-rose-50 px-2 py-1 font-medium text-rose-700">
                                                  Absent {fmtMins(s.absent)}
                                                </span>
                                              </div>
                                            )}
                                          </div>

                                          {/* hour-by-hour for this shift: every hour of the shift is listed */}
                                          <div className="overflow-x-auto">
                                            <table className="w-full min-w-[560px] text-sm">
                                              <thead className="border-b border-slate-100 bg-slate-50/40">
                                                <tr>
                                                  <Th>Hour</Th>
                                                  <Th className="w-2/5">Present vs absent</Th>
                                                  <Th>Present (min)</Th>
                                                  <Th>Absent (min)</Th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-slate-100">
                                                {s.hours.map((h) => (
                                                  <tr key={h.hour}>
                                                    <td className="whitespace-nowrap px-4 py-2.5 font-medium text-slate-700">{h.label}</td>
                                                    <td className="px-4 py-2.5">
                                                      <SplitBar present={h.present} absent={h.absent} capacity={h.cap} />
                                                    </td>
                                                    <td className="px-4 py-2.5 text-emerald-600">{h.present}</td>
                                                    <td className="px-4 py-2.5 text-rose-600">{h.absent}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* ---------------- Alerts table ---------------- */}
            <SectionCard
              icon={<AlertOutlined />}
              title="Writer Absence Alerts"
              subtitle={`${filteredAlerts.length} alert${filteredAlerts.length === 1 ? "" : "s"} for ${
                activeDate ? dateLabel(activeDate) : "—"
              }, newest first.`}
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                  <thead className="border-b border-slate-100 bg-slate-50/40">
                    <tr>
                      <Th>Date</Th>
                      <Th>Time</Th>
                      <Th>Store</Th>
                      <Th>What happened</Th>
                      <Th>Snapshot</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAlerts.length === 0 ? (
                      <EmptyRow
                        ok
                        colSpan={5}
                        title={alerts.length === 0 ? "No writer alerts right now" : "No alerts match your filters"}
                        text={
                          alerts.length === 0
                            ? `No writer absence detected at ${store}.`
                            : "Try a different search, camera or date."
                        }
                      />
                    ) : (
                      pagedAlerts.map((a) => (
                        <tr key={a.id} className="transition hover:bg-rose-50/30">
                          <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(a.time)}</td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <p className="font-medium text-slate-800">{fmtTime(a.time)}</p>
                          </td>
                          <td className="px-4 py-3 font-medium text-slate-800">{a.restaurant}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                              Writer Absent
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setSelected(a)}
                              className="group flex items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
                              aria-label={`View snapshot for ${a.restaurant}`}
                            >
                              <AlertImage src={a.image} alt="Snapshot" className="h-10 w-16 rounded-md border border-slate-200" />
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 group-hover:underline">
                                <EyeOutlined /> View
                              </span>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={safeAlertPage}
                pageSize={ALERTS_PAGE_SIZE}
                total={filteredAlerts.length}
                onChange={setAlertPage}
              />
            </SectionCard>
          </>
        )}

        {/* ---------------- Writer Food Alerts table (antd Table) ---------------- */}
        <SectionCard
          icon={<CoffeeOutlined />}
          title="Writer Food Alerts"
          subtitle={`${filteredFood.length} food alert${filteredFood.length === 1 ? "" : "s"} for ${
            activeDate ? dateLabel(activeDate) : "—"
          }, newest first.`}
        >
          {foodError && (
            <div className="flex items-start justify-between gap-3 border-b border-rose-100 bg-rose-50/60 px-4 py-3 text-sm text-rose-700 sm:px-5">
              <p className="flex items-start gap-2">
                <ExclamationCircleOutlined className="mt-0.5" /> {foodError}
              </p>
              <button
                type="button"
                onClick={() => fetchFood()}
                className="shrink-0 rounded-lg bg-white px-3 py-1 text-xs font-medium text-rose-600 ring-1 ring-rose-200 transition hover:bg-rose-50"
              >
                Try again
              </button>
            </div>
          )}

          <Table
            rowKey="id"
            columns={foodColumns}
            dataSource={filteredFood}
            loading={foodLoading}
            scroll={{ x: 820 }}
            locale={{
              emptyText: (
                <EmptyBlock
                  ok={!foodError && foodAlerts.length === 0}
                  title={foodAlerts.length === 0 ? "No writer food alerts" : "No food alerts match your search"}
                  text={
                    foodAlerts.length === 0
                      ? `No food alerts recorded at ${store} for ${activeDate ? dateLabel(activeDate) : "this date"}.`
                      : "Try a different search."
                  }
                />
              ),
            }}
            pagination={{
              current: foodPage,
              pageSize: foodPageSize,
              total: filteredFood.length,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
              showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
              style: { margin: "12px 16px" },
              onChange: (page, size) => {
                setFoodPage(page);
                setFoodPageSize(size);
              },
            }}
          />
        </SectionCard>
      </div>

      {selected && (
        <ImageModal alert={selected} onClose={() => setSelected(null)} alt="Writer Absent Snapshot" />
      )}
      {selectedFood && (
        <ImageModal
          alert={selectedFood}
          onClose={() => setSelectedFood(null)}
          alt={selectedFood.foodName}
          details={[
            ["Store", selectedFood.restaurant],
            ["Food name", selectedFood.foodName],
            ["Date", fmtDate(selectedFood.time)],
            ["Time", fmtTime(selectedFood.time)],
          ]}
        />
      )}
    </div>
  );
};

export default EmpireWriterAlerts;